import { forwardRef, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, type CardHeaderProps, type CardProps, Divider, styled, type SxProps, type Theme, Typography } from '@mui/material';

export type MainCardProps = Omit<CardProps, 'content'> & {
    border?: boolean | undefined;
    boxShadow?: boolean | undefined;
    content?: boolean | undefined;
    contentSx?: SxProps<Theme> | undefined;
    darkTitle?: boolean | undefined;
    divider?: boolean | undefined;
    modal?: boolean | undefined;
    secondary?: ReactNode | undefined;
    shadow?: string | undefined;
    subheader?: ReactNode | string | undefined;
    title?: ReactNode | string | undefined;
};

export const MainCard = forwardRef<HTMLDivElement, MainCardProps>(({
    children,
    content = true,
    contentSx = {},
    darkTitle,
    divider = true,
    secondary,
    subheader,
    title,
    ...cardProps
}, ref) => {

    return (
        <MainCardRoot elevation={0} ref={ref} {...cardProps}>
            {!darkTitle && <MainCardHeader title={title} action={secondary} subheader={subheader} />}
            {!darkTitle && title && divider && <Divider />}
            {darkTitle && title && <MainCardHeader action={secondary} title={
                <Typography variant="h4">{title}</Typography>
            } />}
            {content && <CardContent sx={contentSx}>{children}</CardContent>}
            {!content && children}
        </MainCardRoot>
    );
});

const MainCardHeader = ({ title, ...props }: CardHeaderProps) => title
    ? (
        <CardHeader
            {...props}
            title={title}
            slotProps={{ title: { variant: 'subtitle1' } }}
            sx={{ p: 2.5, '& .MuiCardHeader-action': { m: '0px auto', alignSelf: 'center' } }}
        />
    )
    : null;

const MainCardRoot = styled(Card, {
    shouldForwardProp: prop => prop !== 'border' && prop !== 'boxShadow' && prop !== 'modal' && prop !== 'shadow',
})<MainCardProps>(
    ({ border = true, boxShadow, modal, shadow, theme }) => ({
        position: 'relative',
        border: border ? '1px solid' : 'none',
        borderRadius: 1,
        borderColor: theme.palette.grey.A800,
        boxShadow: boxShadow && !border ? shadow || theme.customShadows.z1 : 'inherit',
        '&:hover': { boxShadow: boxShadow ? shadow || theme.customShadows.z1 : 'inherit' },
        ...(modal && {
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `calc( 100% - 50px)`,
            [theme.breakpoints.up('sm')]: {
                width: 'auto',
            },
            '& .MuiCardContent-root': {
                overflowY: 'auto',
                minHeight: 'auto',
                maxHeight: `calc(100vh - 200px)`
            }
        }),
    })
);
